import { IDeferred, CompatibilityService, IQService } from './compatibilityservice';
import { fn } from '../utils';

/** Provider Handling NAV Url access */
export class NavUrlService implements IHasNavUrl {
    constructor(private readonly $q: CompatibilityService, defaultUrl?: TNavUrlAccess) {
        this.defaultUrl = $q.defer<INavUrlInfo>();
        if (defaultUrl)
            this.SetDefaultUrl(defaultUrl);
    }

    /** Set application wide default Navision URL access, must be done only once!
    * (Would be more precise to use config-NavUrlServiceProvider.NavUrl only)
    */
    public SetDefaultUrl(externalUrl: TNavUrlSource): PromiseLike<INavUrlInfo> {
        let $q = this.$q;
        if (externalUrl === null || externalUrl === undefined || externalUrl instanceof NavUrlService)
            return $q.reject("DefaultUrl invalid");
        if (this.externalDefaultUrl)
            return $q.reject("DefaultUrl must only be set once!");
        this.externalDefaultUrl = externalUrl;
        try {
            this.defaultUrl.resolve(<any>$q.when(ResolveNavUrl(externalUrl)));
        } catch (err) {
            this.defaultUrl.reject(err);
        }
        return this.NavUrl;
    }
    private readonly defaultUrl: IDeferred<INavUrlInfo>;

    /** Externally Attached Default Url (might not be resolved!) */
    public get ExternalDefaultUrl() {
        return this.externalDefaultUrl;
    }
    private externalDefaultUrl: TNavUrlSource;

    // Resolve Url Info (undefined : GetDefault Url)
    public ResolveUrl(urlInfo?: TNavUrlAccess | null | undefined, validateCompany?: boolean): PromiseLike<INavUrlInfo> {
        return this.doResolveUrl([], urlInfo, validateCompany);
    }

    private doResolveUrl(recItems: any[], urlInfo?: TNavUrlAccess | null | undefined, validateCompany?: boolean): PromiseLike<INavUrlInfo> {
        if (urlInfo === null || urlInfo === undefined || urlInfo == void 0)
            return this.NavUrl;

        let $q = this.$q;

        if (typeof urlInfo === "string")
            return $q.when().then(() => validate(ResolveNavUrl(urlInfo, validateCompany)));

        else if (IsNavUrlInfo(urlInfo))
            return $q.when(urlInfo);

        else {
            recItems.push(urlInfo);
            let start = $q.when(HasNavUrl(urlInfo) ? urlInfo.NavUrl : urlInfo);

            return start.then(uInfo => {
                checkAllowed(uInfo);
                if (HasNavUrl(uInfo)) {
                    return this.doResolveUrl(recItems, checkAllowed(uInfo.NavUrl), validateCompany);
                }
                else {
                    var urlInfo = typeof uInfo === "string" ? ResolveNavUrl(uInfo) : uInfo;
                    return validate(urlInfo);
                }
            });
        }

        function HasNavUrl(u: any): u is IHasNavUrl {
            return typeof u === "object" && ("NavUrl" in u);
        }

        function validate(urlInfo: INavUrlInfo): INavUrlInfo {
            return validateUrl(urlInfo, validateCompany);
        }

        function checkAllowed<T>(item: T) {
            if (recItems.indexOf(item) >= 0) {
                throw new Error("ResolveNavUrl : Recursion!");
            }
            return item;
        }
    }

    /** NAV Url get, to be called after 'SetDefaultUrl' only returns default url*/
    public get NavUrl(): PromiseLike<INavUrlInfo> {
        if (!this.ExternalDefaultUrl) {
            return this.$q.reject("NavUrl not yet set on NavUrlService");
        }
        return this.defaultUrl.promise;
    }
}

// Resolve Url Info (undefined : GetDefault Url)
/** Resolve Nav Url Info from string, or promise, ... */
export function ResolveNavUrl(me: string | INavUrlInfo, validateCompany?: boolean): INavUrlInfo;
export function ResolveNavUrl(me: TNavUrlSource, validateCompany?: boolean): PromiseLike<INavUrlInfo> | INavUrlInfo;
export function ResolveNavUrl(url: TNavUrlSource, validateCompany: boolean): PromiseLike<INavUrlInfo> | INavUrlInfo {
    if (url == undefined)
        return validate(<any>url);

    else if (typeof url == "string")
        return validate(resolveString(url));

    else if (IsNavUrlInfo(url))
        return validate(url);

    else if (fn.isPromise(url))
        return resolvePromise(url);

    else if (HasNavUrl(url))
        return resolveHas(url);

    else
        return resolveFullNavUrl(url, (<any>url).$q);

    function resolvePromise(me: PromiseLike<TNavUrl>, $q?: IQService): PromiseLike<INavUrlInfo> {
        var pResult = me.then(u => {
            if (HasNavUrl(u)) {
                return resolveHas(u, $q);
            }

            return validate(typeof u == "string" ? resolveString(u) : u);
        });
        return pResult;
    }

    function resolveHas(has: IHasNavUrl, $q?: IQService) {
        var innerInfo = has.NavUrl;
        if (fn.isPromise(innerInfo))
            return resolvePromise(innerInfo, $q);
        else
            return resolveOpt(innerInfo, $q);
    }

    function resolveOpt(opt: TNavUrl, $q?: IQService): INavUrlInfo | PromiseLike<INavUrlInfo> {
        if (typeof opt === "string")
            return resolveString(opt);

        if (HasNavUrl(opt)) {
            return resolveHas(opt, $q);
        }

        return opt;
    }

    function resolveFullNavUrl(fullUrl: TNavUrlAccess, $q: IQService): INavUrlInfo | PromiseLike<INavUrlInfo> {
        if (fn.isPromise(fullUrl))
            return fullUrl.then(r => resolveOpt(r, $q));

        if (!(HasNavUrl(fullUrl) || typeof fullUrl === "string" || IsNavUrlInfo(fullUrl))) {
            throw new Error("Failed to resolve NavUrl from " + JSON.stringify(fullUrl));
        }
        if (!$q)
            throw new Error("Failed to Resolve Url : Missing $q Context!");

        return $q.when(fullUrl).then(optUrl => {
            if (HasNavUrl(optUrl))
                return $q.when(resolveFullNavUrl(optUrl.NavUrl, $q));
            if (typeof optUrl == "string")
                return validate(resolveString(optUrl));
            else
                return validate(optUrl);
        });
    }

    function resolveString(fullUrl: string): INavUrlInfo {
        if (typeof fullUrl !== "string")
            throw new Error("Expected string to resolve url");
        // handle ".../ws" -> ".../ws/" and   "/ws?..." -> "/ws/?..." for esier handling
        if (/\/ws\?/i.test(fullUrl))
            fullUrl = fullUrl.replace(/\/ws\?/i,"/ws/?" )
        if (/\/ws$/i.test(fullUrl))
            fullUrl = fullUrl + "/";

        var reMandant = (new RegExp("/ws/[^(\/|\\?|$)*]*", "i")).exec(fullUrl),
            reBaseUrl = (new RegExp(".*/ws(/|\\?|$)", "i")).exec(fullUrl),
            reQueryString = (new RegExp(".*(\\?.*)", "i")).exec(fullUrl),
            //reTenant = (new RegExp(".*((\\?tenant)|(\&tenant))=([^\&]+)", "i")).exec(fullUrl),
            reWithCompany = (new RegExp(".*/ws/[^(\/|\?)*]*", "i")).exec(fullUrl);

        if (!([reBaseUrl, reMandant, reWithCompany]).every(re => re && (re.length > 0)))
            throw new Error("Ungültige Konfiguration 'NavUrl[*/ws(/|?)*]' : '" + fullUrl + "'");

        const QueryString = (reQueryString && reQueryString[1]) || '';
        var result = {
            FullUrl: reWithCompany[0] + QueryString,
            Mandant: reMandant[0]?.replace(/\/ws(\/|\?)/i, ""),
            BaseUrl: reBaseUrl[0].replace(/\/ws(\/|\?)/i, ""),
            QueryString : QueryString
        };

        return result;
    }

    function HasNavUrl(test: any): test is IHasNavUrl {
        return typeof test === "object" && ("NavUrl" in test);
    }

    function validate(urlInfo: INavUrlInfo): INavUrlInfo {
        return validateUrl(urlInfo, validateCompany);
    }
}

function validateUrl(urlInfo: INavUrlInfo, validateCompany: boolean): INavUrlInfo {
    if (!urlInfo) {
        throw new Error("No Navision URL provided!");
    }

    if (!urlInfo.Mandant && validateCompany) {
        throw new Error("No Company for NAV provided!");
    }

    if (!urlInfo.FullUrl || !urlInfo.BaseUrl) {
        throw new Error("Invalid Default Nav Url");
    }

    return urlInfo;
}

function IsNavUrlInfo(test: any): test is INavUrlInfo {
    var check: INavUrlInfo = test;
    var result = check && check.BaseUrl && check.FullUrl && (typeof check.Mandant === "string");
    return result;
}

/** Construct NavUrl with given Company */
export function BuildNavUrl(fullUrl: string | INavUrlInfo, mandant: string) {
    var info = ResolveNavUrl(fullUrl, false);
    return info && info.BaseUrl && (info.BaseUrl + "/ws/" + (mandant || "") + (info?.QueryString || ""));
}

/** possible Input to Resolve a Full Nav Url */
export interface IHasNavUrl {
    /** Indirect Acess to NavUrl */
    NavUrl: TNavUrlAccess;
}
/** Ways to describe input to resolve a NavUrl */
export type TNavUrl = string | INavUrlInfo | IHasNavUrl;
/** Possible Inputs to Resolve a Full Nav Url, including Company */
export type TNavUrlAccess = TNavUrl | PromiseLike<TNavUrl>;
/** All Ways that can be resolved to a Nav Url */
export type TNavUrlSource = TNavUrlAccess & ({ $q?: IQService });

export interface INavUrlInfo {
    FullUrl: string;
    BaseUrl: string;
    Mandant: string;
    QueryString: string;
}

export interface INavUrlInfoEx extends INavUrlInfo {
    /** Config for the URL */
    Config: string;
    /** Display Name */
    Name?: string;
}
